import { getHours } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { StorePerformanceInterval } from '../constants';
import { KpiDistribution, StorePerformanceItem } from '../hooks';
import { getIsWithinOperationHours } from './storeOrMarketUtils';
import { getFormattedTimeUnit } from './formatter';

export const getXAxisLabel = (interval: StorePerformanceInterval, item: StorePerformanceItem) => {
  if (interval === StorePerformanceInterval.WEEKLY) {
    return item.timeUnit;
  }

  const { timeUnit } = item;
  const hour = Number.parseInt(timeUnit);
  return `${hour}`;
};

export const getXAxisLabels = (
  interval: StorePerformanceInterval,
  storePerformanceItems: StorePerformanceItem[],
) => {
  if (interval === StorePerformanceInterval.WEEKLY || interval === StorePerformanceInterval.MONTHLY) {
    return storePerformanceItems.map((item) => {
      return getFormattedTimeUnit(item.timeUnit, interval);
    });
  }

  return storePerformanceItems.map((item) => {
    const { timeUnit } = item;
    const hour = Number.parseInt(timeUnit);
    return `${hour}`;
  });
};

export const getClippedDailyPerformanceItems = <T extends StorePerformanceItem>(params: {
  interval: StorePerformanceInterval;
  storeOpeningHour: number;
  storeClosingHour: number;
  storePerformanceItems: T[];
  getHasValue: (
    storePerformanceItem: T,
    interval?: StorePerformanceInterval,
    storeOpeningHour?: number,
    storeClosingHour?: number,
  ) => boolean;
}) => {
  const { interval, storeOpeningHour, storeClosingHour, storePerformanceItems, getHasValue } = params;
  if (interval === StorePerformanceInterval.WEEKLY) {
    return [...storePerformanceItems].sort((a, b) => (Date.parse(a.timeUnit) < Date.parse(b.timeUnit) ? -1 : 1));
  }

  return storePerformanceItems.filter((item) => {
    // prevent filtering out performace items if they have any value at a given timeUnit
    if (getHasValue(item, interval, storeOpeningHour, storeClosingHour)) return true;

    return getIsWithinOperationHours(item.timeUnit, storeOpeningHour, storeClosingHour);
  }) as T[];
};

export const getTodayPerformanceItems = <T extends StorePerformanceItem>(params: {
  interval: StorePerformanceInterval;
  currentUTCTime: Date;
  storeTimezone: string;
  storePerformanceItems: T[];
  getHasValue: (storePerformanceItem: T) => boolean;
}): (T | null)[] => {
  const { interval, currentUTCTime, storeTimezone, storePerformanceItems, getHasValue } = params;

  if (interval === StorePerformanceInterval.WEEKLY) return storePerformanceItems;

  const zonedDate = utcToZonedTime(currentUTCTime, storeTimezone);

  const zonedHour = getHours(zonedDate);

  return storePerformanceItems.map((item) => {
    const { timeUnit } = item;

    if (getHasValue(item)) return item;

    if (Number.parseInt(timeUnit) > zonedHour) {
      return null;
    }

    return item;
  });
};

export const getForecastPerformanceItems = <T extends StorePerformanceItem>(params: {
  interval: StorePerformanceInterval;
  performanceItems: T[];
  filteredPerformanceItems: (T | null)[];
  forecastItems: T[];
}): (T | null)[] => {
  const { interval, performanceItems, filteredPerformanceItems, forecastItems } = params;

  if (interval === StorePerformanceInterval.WEEKLY) return [];

  const forecastMap = new Map(forecastItems.map((item) => [item.timeUnit, item]));

  const filteredForecastItems = filteredPerformanceItems.map((item, index) => {
    if (item != null) return null;

    const performanceItem = performanceItems[index];

    const forecastItem = forecastMap.get(performanceItem.timeUnit);

    return forecastItem ?? null;
  });

  return filteredForecastItems;
};

export const getClippedTargetItems = (params: {
  performanceItems: StorePerformanceItem[];
  kpiDistribution: KpiDistribution[];
}) => {
  const { performanceItems, kpiDistribution } = params;

  const kpiDistributionMap = new Map(kpiDistribution.map((item) => [item.timeUnit, item]));
  return performanceItems.map((item) => {
    const kpiDistributionItem = kpiDistributionMap.get(item.timeUnit);
    return kpiDistributionItem ? kpiDistributionItem.value : null;
  });
};
